export const RESET_PERIOD_HOURS = 720; // Default 30 days
export const DEFAULT_RESET_PERIOD_DAYS = 30;

export const USAGE_FEATURES = {
  ANALYTICS_VIEW: 'analytics_views',
  BIQ_QUERY: 'biq_tokens',
  BIQ_DEEP_THINKING: 'deep_thinking_tokens',
  REPORT_GENERATION: 'pdf_reports',
  DEFAULT_ALERT_CREATE: 'default_alerts',
  CUSTOM_ALERT_CREATE: 'custom_alerts',
};

// Fallback limits if API fails
export const FALLBACK_USAGE_LIMITS = {
  free: {
    [USAGE_FEATURES.BIQ_QUERY]: 900000,
    [USAGE_FEATURES.BIQ_DEEP_THINKING]: 0,
    [USAGE_FEATURES.ANALYTICS_VIEW]: 50,
    [USAGE_FEATURES.REPORT_GENERATION]: 1,
    [USAGE_FEATURES.DEFAULT_ALERT_CREATE]: 1,
    [USAGE_FEATURES.CUSTOM_ALERT_CREATE]: 0,
  },
  basic: {
    [USAGE_FEATURES.BIQ_QUERY]: 6825000,
    [USAGE_FEATURES.BIQ_DEEP_THINKING]: 925926,
    [USAGE_FEATURES.ANALYTICS_VIEW]: 500,
    [USAGE_FEATURES.REPORT_GENERATION]: 10,
    [USAGE_FEATURES.DEFAULT_ALERT_CREATE]: 5,
    [USAGE_FEATURES.CUSTOM_ALERT_CREATE]: 5,
  },
  advanced: {
    [USAGE_FEATURES.BIQ_QUERY]: 21500000,
    [USAGE_FEATURES.BIQ_DEEP_THINKING]: 4000000,
    [USAGE_FEATURES.ANALYTICS_VIEW]: 2500,
    [USAGE_FEATURES.REPORT_GENERATION]: 25,
    [USAGE_FEATURES.DEFAULT_ALERT_CREATE]: 10,
    [USAGE_FEATURES.CUSTOM_ALERT_CREATE]: 10,
  },
  elite: {
    [USAGE_FEATURES.BIQ_QUERY]: -1,
    [USAGE_FEATURES.BIQ_DEEP_THINKING]: -1,
    [USAGE_FEATURES.ANALYTICS_VIEW]: -1,
    [USAGE_FEATURES.REPORT_GENERATION]: -1,
    [USAGE_FEATURES.DEFAULT_ALERT_CREATE]: -1,
    [USAGE_FEATURES.CUSTOM_ALERT_CREATE]: -1,
  },
  ifpa: {
    [USAGE_FEATURES.BIQ_QUERY]: 36400000,
    [USAGE_FEATURES.BIQ_DEEP_THINKING]: 3703703,
    [USAGE_FEATURES.ANALYTICS_VIEW]: 2500,
    [USAGE_FEATURES.REPORT_GENERATION]: 25,
    [USAGE_FEATURES.DEFAULT_ALERT_CREATE]: 10,
    [USAGE_FEATURES.CUSTOM_ALERT_CREATE]: 10,
  },
};

export const getUsageLimit = (tier, feature, customLimits = null, globalTierLimits = null) => {
  if (customLimits) {
    // Map custom limits keys (from user document) to USAGE_FEATURES
    const mapping = {
      [USAGE_FEATURES.BIQ_QUERY]: customLimits.biq_tokens,
      [USAGE_FEATURES.BIQ_DEEP_THINKING]: customLimits.deep_thinking_tokens,
      [USAGE_FEATURES.ANALYTICS_VIEW]: customLimits.analytics_views,
      [USAGE_FEATURES.REPORT_GENERATION]: customLimits.pdf_reports,
      [USAGE_FEATURES.DEFAULT_ALERT_CREATE]: customLimits.default_alerts,
      [USAGE_FEATURES.CUSTOM_ALERT_CREATE]: customLimits.custom_alerts,
    };

    if (mapping[feature] !== undefined) {
      return mapping[feature] === -1 ? Infinity : mapping[feature];
    }
  }

  const limitsSource = globalTierLimits || FALLBACK_USAGE_LIMITS;
  const tierLimits = limitsSource[tier] || limitsSource.free;
  const limit = tierLimits[feature] ?? 0;

  return limit === -1 ? Infinity : limit;
};

export const isUnlimited = (limit) => limit === Infinity || limit === -1;

export const getTimeUntilReset = (lastResetTimestamp, resetPeriodDays = DEFAULT_RESET_PERIOD_DAYS) => {
  const resetPeriodMs = resetPeriodDays * 24 * 60 * 60 * 1000;
  const nextResetTime = lastResetTimestamp + resetPeriodMs;
  const now = Date.now();
  const msUntilReset = Math.max(0, nextResetTime - now);

  const hours = Math.floor(msUntilReset / (1000 * 60 * 60));
  const minutes = Math.floor((msUntilReset % (1000 * 60 * 60)) / (1000 * 60));

  return { hours, minutes, msUntilReset };
};

export const formatTimeUntilReset = (lastResetTimestamp, resetPeriodDays = DEFAULT_RESET_PERIOD_DAYS) => {
  const { hours, minutes } = getTimeUntilReset(lastResetTimestamp, resetPeriodDays);

  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''}`;
  }

  if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} min`;
  }

  return `${minutes} minute${minutes > 1 ? 's' : ''}`;
};
